import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import queryClient from '@/lib/queryClient'
import { indexTableQueryOptions } from '@/lib/services/index-table-query-options'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query'
import type { AuthState } from '../auth/auth.api'
import { authQueryKeys } from '../auth/auth.query'
import {
  createUser,
  getUser,
  getUsers,
  softDeleteUser,
  updateUser,
  type UserOrganizationScope,
  type UserScope,
} from './users.api'
import type { CreateUserInput, UpdateUserInput, UserIdInput } from './users.types'

interface OrganizationScope {
  organizationId?: OrganizationIdInput
}

/** The key entries each fetcher reads; the scoped key builders below set them. */
type UserIndexQueryKey = [AdminQueryRoot & UserOrganizationScope]
type UserShowQueryKey = [AdminQueryRoot & UserScope]

/** Query options a caller may override; the key and fetcher stay owned by this module. */
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

/** Updates take the id of the user they change with the request fields. */
interface UserMutationValues {
  userId: UserIdInput
}

export const userQueryKeys = {
  all: ({ organizationId }: OrganizationScope = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'user' }, { organizationId }),
  ],
  index: ({ organizationId }: OrganizationScope = {}): UserIndexQueryKey =>
    buildScopedAdminQueryKey(userQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
    }),
  show: ({ organizationId, userId }: UserScope): UserShowQueryKey =>
    buildScopedAdminQueryKey(userQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      userId,
    }),
}

export const userQueries = {
  index: ({ organizationId }: OrganizationScope = {}) =>
    indexTableQueryOptions({
      queryKey: userQueryKeys.index({ organizationId }),
      queryFn: getUsers,
    }),

  show: ({ organizationId, userId }: UserScope) =>
    queryOptions({
      queryKey: userQueryKeys.show({ organizationId, userId }),
      queryFn: getUser,
      // Use data from the product index route if available
      initialData: () =>
        queryClient
          .getQueryData(userQueries.index({ organizationId }).queryKey)
          ?.find((u) => u.id === userId),
      initialDataUpdatedAt: () =>
        queryClient.getQueryState(userQueryKeys.index({ organizationId }))?.dataUpdatedAt,
    }),
}

export function useUsers({
  organizationId,
  params = {},
}: OrganizationScope & { params?: QueryParams<ReturnType<typeof userQueries.index>> } = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...userQueries.index({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useUser(
  userId: UserIdInput | undefined,
  {
    organizationId,
    ...params
  }: OrganizationScope & QueryParams<ReturnType<typeof userQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...userQueries.show({ organizationId: resolvedOrganizationId, userId }),
    ...params,
  })
}

export function useCreateUserMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: CreateUserInput) => createUser({ organizationId, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: userQueryKeys.all({ organizationId }) }),
  })
}

export function useUpdateUserMutation() {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (values: UpdateUserInput & UserMutationValues) =>
      updateUser({ organizationId, ...values }),
    onSuccess: (response) => {
      const promises: Promise<void>[] = []

      promises.push(
        queryClient.invalidateQueries({ queryKey: userQueryKeys.all({ organizationId }) })
      )

      // If the user just updated their own profile, update the auth state
      const auth = queryClient.getQueryData<AuthState>(authQueryKeys.user())
      if (auth?.user?.id === response?.id) {
        promises.push(queryClient.invalidateQueries({ queryKey: authQueryKeys.all }))
      }

      return Promise.all(promises)
    },
  })
}

const cleanupDeletedUserQueries = ({
  organizationId,
  userId,
}: {
  organizationId: number
  userId: UserIdInput
}) => {
  queryClient.removeQueries({ queryKey: userQueryKeys.show({ organizationId, userId }) })

  return queryClient.invalidateQueries({ queryKey: userQueryKeys.all({ organizationId }) })
}

export function useSoftDeleteUserMutation({
  cleanupOnSuccess = true,
}: { cleanupOnSuccess?: boolean } = {}) {
  const organizationId = useRouteOrganizationId()
  const cleanupDeletedUser = (userId: UserIdInput) =>
    cleanupDeletedUserQueries({ organizationId, userId })

  const mutation = useMutation({
    mutationFn: (userId: UserIdInput) => softDeleteUser({ organizationId, userId }),
    onSuccess: (_, userId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedUser(userId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedUser,
  }
}
